/**
 * Eine Zeile Code, wie diese Anwendung sie zeigt — der eine Baustein für **alle**
 * Darstellungen von Code.
 *
 * Er steht hier und nicht in der Revisionsansicht, weil es drei Orte gibt, die
 * Code zeigen: den Diff, die vollständige Datei und den **Ausschnitt** am Kopf einer
 * Stelle. Zwei Fassungen desselben Bausteins wären zwei Gelegenheiten, dieselbe
 * Zeile verschieden breit, verschieden getönt und mit verschieden gesetzten
 * Nummern zu zeichnen — und ausgerechnet der Ausschnitt und der Diff stehen
 * untereinander, wo der Unterschied sofort auffiele.
 *
 * **Anklickbar ist sie nur, wo markiert wird.** Mit `onPick` ist sie eine
 * Schaltfläche: Markieren ist dort die Hauptsache, und was man anklickt, muss auch
 * mit der Tastatur erreichbar sein. Ohne `onPick` ist sie eine Zeile Text — im
 * Ausschnitt ist die Stelle bereits markiert, und eine zweite Markierung an derselben
 * Sache wäre ein zweiter Weg mit einem anderen Ergebnis.
 *
 * **Sie bekommt fertige Token und keinen Text** (ADR-0026). Getönt wird im Bündel,
 * von einer reinen Funktion (`highlight.ts`), je Dateiänderung einmal; dieser
 * Baustein behält seine eine Aufgabe — eine Zeile zeichnen — und entscheidet über
 * Farben nur noch, welches Token welche Rolle trägt.
 *
 * **Gekürzt wird sie nicht.** Ihr Text steht als `white-space: pre` und ohne
 * `text-overflow`; was breiter ist als die Fläche, wird waagerecht erreichbar und
 * nicht abgeschnitten (`CodeLines`). Wer eine Zeile befundet, die er nur zur
 * Hälfte sieht, befundet blind.
 *
 * Gesetzt ist sie dicktengleich, `var(--font-mono)` — die Kopie des Skills führt
 * das Token seit #36 (`frontend/src/design/tokens/typography.css`) neben der
 * proportionalen `--font-sans`. Einrückung, Ausrichtung und Zeichenzählung tragen
 * in einer Proportionalschrift nicht; das war der Grund, den die proportionale
 * Schrift hier vor #36 hatte, und mit dem Token liegt er nicht mehr vor (#107). Die Größe steht
 * eine Stufe unter dem Fließtext, `--fs-11` statt `--fs-12`: Eine dicktengleiche
 * Schrift läuft bei gleicher Punktgröße breiter und wirkt neben Inter größer, und
 * die Stufe darunter gleicht das im Bild aus, ohne die Ziffern der Spalte zu
 * verkleinern. Die laufen weiter als `tabular-nums`, damit die Nummern
 * untereinander stehen — bei einer dicktengleichen Schrift redundant, aber nicht
 * falsch, und ohne Kosten.
 */

import type { CSSProperties, ReactElement, ReactNode } from "react";

import type { DiffLine } from "../../api/client";
import { formatNumber } from "../../formatting";
import type { Role, Token } from "./highlight";

/** Die beiden Nummern einer Zeile: links die alte Seite, rechts die neue. */
export type Gutter = [number | null, number | null];

/**
 * Die Farbe je Rolle (ADR-0026). `plain` steht nicht darin: Bezeichner,
 * Funktions- und Klassennamen, Typen, Klammern und Operatoren erben `--text-body`
 * von der Zeile, und ein eigenes Token dafür wäre eines zu viel.
 */
const COLOR: Partial<Record<Role, string>> = {
  keyword: "var(--code-keyword)",
  string: "var(--code-string)",
  number: "var(--code-number)",
  comment: "var(--code-comment)",
  variable: "var(--code-variable)",
};

export function CodeLine({
  label,
  gutter,
  sign,
  tokens,
  tint,
  marked,
  onPick,
}: {
  label: string;
  gutter: Gutter;
  sign: string;
  /** Die Zeile, schon getönt — gerechnet wird das im Bündel und je Dateiänderung einmal. */
  tokens: Token[];
  tint: DiffLine["kind"];
  marked: boolean;
  /** Fehlt er, ist die Zeile nur zu lesen — kein Knopf, keine Tastaturmarke. */
  onPick?: (extend: boolean) => void;
}): ReactElement {
  const content = (
    <>
      {gutter.map((number, index) => (
        <span
          key={index}
          aria-hidden="true"
          style={{
            flex: "none",
            width: 44,
            textAlign: "right",
            color: "var(--text-meta)",
            fontFamily: "var(--font-sans)",
            fontSize: "var(--fs-11)",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {number ?? ""}
        </span>
      ))}

      <span aria-hidden="true" style={{ flex: "none", color: "var(--fg-6)" }}>
        {sign}
      </span>

      {/*
        Eine leere Zeile trägt ein Leerzeichen, sonst fiele ihre Höhe zusammen.

        `flex: none`, damit der Text auch dann nicht schrumpft, wenn die Zeile
        schmaler ist als er — geschrumpft würde er auf Kosten der Zeichen am Ende,
        und die sind der Grund, warum diese Fläche waagerecht rollt.
      */}
      <span style={{ flex: "none", whiteSpace: "pre" }}>
        {tokens.every((token) => token.text === "")
          ? " "
          : tokens.map((token, index) => (
              <span key={index} style={colorOf(token.role)}>
                {token.text}
              </span>
            ))}
      </span>
    </>
  );

  const shape = {
    display: "flex",
    alignItems: "baseline",
    gap: "var(--sp-4)",
    width: "100%",
    padding: "0 var(--sp-6)",
    border: "none",
    borderLeft: `1px solid ${marked ? "var(--ac-blue)" : "transparent"}`,
    background: backgroundOf(tint, marked),
    color: "var(--text-body)",
    fontFamily: "var(--font-mono)",
    fontSize: "var(--fs-11)",
    lineHeight: "var(--lh-normal)",
    textAlign: "left" as const,
  };

  if (onPick === undefined) {
    return (
      // `aria-current` und nicht `aria-pressed`: Gedrückt ist hier nichts, aber
      // eine dieser Zeilen ist die, um die es geht — und das ist auch zu hören,
      // wenn die Tönung nicht zu sehen ist.
      <div aria-label={label} aria-current={marked ? "true" : undefined} style={shape}>
        {content}
      </div>
    );
  }

  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={marked}
      onClick={(event) => onPick(event.shiftKey)}
      style={{ ...shape, cursor: "pointer" }}
    >
      {content}
    </button>
  );
}

/**
 * Die Fläche, in der Codezeilen stehen — **so breit wie ihre längste**, mindestens
 * so breit wie der Kasten darüber.
 *
 * Sie steht hier, weil ohne sie jede Zeile so breit ist wie der Kasten: Was nicht
 * hineinpasst, ist dann nicht erreichbar. **Umgebrochen wird nicht** — eine
 * umgebrochene Codezeile wäre eine Zeile, die es in der Datei nicht gibt, und der
 * Anker eines Befundes nennt Zeilennummern (ADR-0006). Also wächst die Fläche, und
 * gerollt wird waagerecht.
 *
 * **Ein Rollbereich für alle Zeilen, nicht einer je Zeile.** Gerollt wird in dem
 * Kasten, der auch senkrecht rollt; diese Fläche wächst darin. Je Zeile ein eigener
 * Rollbereich hieße mehrere Rollstände nebeneinander — und damit Nummernspalten, die
 * nicht mehr untereinander stehen.
 *
 * `width: max-content` **und** `min-width: 100%`, beides gebraucht: Das erste macht
 * die Fläche so breit wie ihre längste Zeile, wodurch die Zeilen darin — sie sind
 * `width: 100%` — alle gleich breit werden. Das zweite füllt den Kasten, wenn keine
 * Zeile ihn ausfüllt; sonst endete die Tönung einer Zeile mitten in der Fläche.
 *
 * **Die Nummernspalte wandert mit, sie bleibt nicht stehen.** Gegen `position:
 * sticky` entschieden, und zwar hier aufgeschrieben, weil beides vertretbar wäre:
 * Eine stehende Spalte braucht einen deckenden Untergrund, und der Untergrund einer
 * Zeile ist in diesem System durchscheinend (`color-mix(… transparent)`, ADR-0026).
 * Der Code liefe sichtbar unter den Nummern durch, und die Farbe der Spalte hinge an
 * der Fläche darunter, die die Zeile nicht kennt. Mitwandern ist die kleinere
 * Entscheidung: Die Nummer gehört zu ihrer Zeile, alle Zeilen tragen denselben
 * Rollstand, und zurück nach links ist ein Wisch.
 */
export function CodeLines({ children }: { children: ReactNode }): ReactElement {
  return <div style={{ width: "max-content", minWidth: "100%" }}>{children}</div>;
}

/** Der Stil einer Token — oder keiner, wo die Zeile ihre Farbe schon hat. */
function colorOf(role: Role): CSSProperties | undefined {
  const color = COLOR[role];

  return color === undefined ? undefined : { color };
}

/**
 * Der Untergrund einer Zeile. Eine Markierung sticht über die Tönung des Diffs:
 * Sie ist die Auswahl, und die soll man auf einer entfernten Zeile ebenso sehen
 * wie auf einer hinzugefügten.
 */
export function backgroundOf(tint: DiffLine["kind"], marked: boolean): string {
  if (marked) {
    return "color-mix(in srgb, var(--ac-blue) 26%, transparent)";
  }

  if (tint === "added") {
    return "color-mix(in srgb, var(--ac-green) 14%, transparent)";
  }

  if (tint === "removed") {
    return "color-mix(in srgb, var(--ac-red) 14%, transparent)";
  }

  return "transparent";
}

/**
 * Wie eine Diff-Zeile heißt. Der Name trägt die Seite mit, weil an ihr die
 * Revision hängt: „entfernte Zeile 5" ist eine andere Stelle als „Zeile 5".
 */
export function labelOfDiffLine(line: DiffLine, number: number | null): string {
  const suffix = number === null ? "" : ` ${formatNumber(number)}`;

  if (line.kind === "removed") {
    return `entfernte Zeile${suffix}`;
  }

  if (line.kind === "added") {
    return `hinzugefügte Zeile${suffix}`;
  }

  return `Zeile${suffix}`;
}
